import { memo, useEffect } from 'react'
import { useI18n } from '@muse/i18n'
import { useValues } from '@core/store'
import { useLogout } from '@features/auth'
import { useCurrentUser } from '@features/session'
import { sendTrySubscriptionClick } from '@features/try-for-free'
import { useLocation } from '@muse-web/router'
import { MobileDrawer } from '../components/mobile-drawer'
import type { THeaderLabels } from '../components/types'
import { buildNavEntries } from '../helpers/buildNavEntries'
import { buildUserMenuItems } from '../helpers/buildUserMenuItems'
import { markActiveNavEntries } from '../helpers/markActiveNavEntries'
import { useHeaderActions } from '../helpers/useHeaderActions'
import { headerStore } from '../store/headerStore'

// The drawer is the nav surface below 1280px; above it the full header nav returns.
const DESKTOP_MEDIA_QUERY = '(min-width: 1280px)'

export const MobileDrawerContainer = memo(() => {
    const { t } = useI18n()
    const { pathname } = useLocation()
    const { username, displayName } = useCurrentUser()
    const [isMobileMenuOpen, mobileMenuSection] = useValues([
        headerStore.props.getIsMobileMenuOpen,
        headerStore.props.getMobileMenuSection,
    ])
    const { isAuthenticated, showTryFree, signInUrl, tryFreeHref, onUpload, closeMobileMenu } =
        useHeaderActions()
    const { logout } = useLogout()

    useEffect(() => {
        closeMobileMenu()
    }, [closeMobileMenu, pathname])

    useEffect(() => {
        const desktop = window.matchMedia(DESKTOP_MEDIA_QUERY)
        const handleDesktop = (event: MediaQueryListEvent) => {
            if (event.matches) {
                closeMobileMenu()
            }
        }

        desktop.addEventListener('change', handleDesktop)

        return () => {
            desktop.removeEventListener('change', handleDesktop)
        }
    }, [closeMobileMenu])

    if (!isMobileMenuOpen) {
        return null
    }

    const handleTryFree = () => {
        sendTrySubscriptionClick({ name: 'header_menu_try_click', payload: {} })
    }

    const handleLogout = () => {
        logout()
        closeMobileMenu()
    }

    const labels: Pick<THeaderLabels, 'upload' | 'tryFree' | 'logOut' | 'new'> = {
        upload: t('i18n.headerUpload'),
        tryFree: t('i18n.headerTryFree'),
        logOut: t('i18n.headerLogOut'),
        new: t('i18n.headerNew'),
    }

    return (
        <MobileDrawer
            navEntries={markActiveNavEntries(buildNavEntries(t, 'hamburger_menu'), pathname)}
            labels={labels}
            signIn={isAuthenticated ? null : { href: signInUrl, label: t('i18n.headerLogIn') }}
            user={
                isAuthenticated
                    ? {
                          displayName: displayName || username,
                          menuItems: buildUserMenuItems(t, username),
                      }
                    : null
            }
            initialSection={mobileMenuSection}
            footer={{ showTryFree, tryFreeHref, onUpload, onTryFree: handleTryFree }}
            onNavigate={closeMobileMenu}
            onLogout={handleLogout}
        />
    )
})
